/**
 * Static mock data transcribed from the team's Excel workbook
 * "GBO-764 (1200 x 90-003-QC; GPO1804).xlsx" for UI prototyping only.
 */

import { CLAMP_QC_PHYSICAL_FAIL_REASONS_BUILTIN } from "@integrator-src/config/clampqc-physical-fail-reasons-builtin";

export interface ClampQcWorkOrderContext {
  workOrder: string;
  drawingRevision: string;
  purchaseOrder: string;
  /** Total units called out on the workbook summary tab (cell value). */
  plannedQuantityFromWorkbook: number;
}

export interface ClampQcInspectionRow {
  /** Legacy workbook field—session rows leave this null (crate not collected). */
  crate: string | null;
  timestampIso: string;
  /**
   * Workbook carton label columns in sample data (`20`, `30`, … carton band sizes per Large vs Small tab conditional formatting).
   * Session rows captured in-app use `0`: operators do not key in carton identifiers (fills track X/Y toward the SKU band size instead).
   */
  boxSerial: number;
  serial: number;
  unloadedLine: number | null;
  loadedLineBeforeTester: number | null;
  loadedLineWithTester?: number | null;
  testerA: boolean;
  testerB: boolean;
  testerC: boolean;
  lineHarvestFail: boolean | null;
  inductance: number | null;
  inductancePassFail: string | null;
  failReason: string | null;
  observations: string | null;
  result: string;
  delta: number | null;
}

export interface ClampQcTimesheetRow {
  dateIso: string;
  timeStart: string;
  timeStop: string;
  initials: string;
  completedUnits: number;
  withLineTest: number;
}

export interface ClampQcInductanceLimits {
  label: string;
  min: number | null;
  max: number | null;
}

/** `Large` worksheet — populated rows from the sample workbook (GBO-764). */
const GBO764_LARGE_INSPECTION_ROWS: ClampQcInspectionRow[] = [
  {
    crate: null,
    timestampIso: "2026-04-27T09:13:23.556",
    boxSerial: 20,
    serial: 2026010754,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:14:04.042",
    boxSerial: 20,
    serial: 2026010753,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.0,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.7,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:14:32.287",
    boxSerial: 20,
    serial: 2026010752,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:14:58.573",
    boxSerial: 20,
    serial: 2026010757,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:15:32.060",
    boxSerial: 20,
    serial: 2026010756,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:15:53.090",
    boxSerial: 20,
    serial: 2026010755,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:16:13.948",
    boxSerial: 20,
    serial: 2026010760,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:16:37.962",
    boxSerial: 20,
    serial: 2026010759,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:17:02.818",
    boxSerial: 20,
    serial: 2026010758,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:17:25.667",
    boxSerial: 20,
    serial: 2026010751,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:17:46.638",
    boxSerial: 20,
    serial: 2026010750,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:18:14.802",
    boxSerial: 20,
    serial: 2026010743,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:18:33.959",
    boxSerial: 20,
    serial: 2026010742,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:19:03.786",
    boxSerial: 20,
    serial: 2026010741,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:19:30.633",
    boxSerial: 20,
    serial: 2026010746,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:19:56.207",
    boxSerial: 20,
    serial: 2026010745,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:20:23.967",
    boxSerial: 20,
    serial: 2026010744,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:20:50.553",
    boxSerial: 20,
    serial: 2026010749,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:21:09.399",
    boxSerial: 20,
    serial: 2026010748,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:21:34.209",
    boxSerial: 20,
    serial: 2026010747,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:35:13.309",
    boxSerial: 30,
    serial: 2026011074,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:35:32.421",
    boxSerial: 30,
    serial: 2026011073,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:35:51.895",
    boxSerial: 30,
    serial: 2026011072,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:36:13.314",
    boxSerial: 30,
    serial: 2026011077,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.4,
    testerA: false,
    testerB: true,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.3,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:37:08.945",
    boxSerial: 30,
    serial: 2026011076,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:37:27.739",
    boxSerial: 30,
    serial: 2026011075,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:37:50.673",
    boxSerial: 30,
    serial: 2026011080,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:38:19.848",
    boxSerial: 30,
    serial: 2026011079,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.1,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.6,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:38:39.080",
    boxSerial: 30,
    serial: 2026011078,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:39:02.453",
    boxSerial: 30,
    serial: 2026011070,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:39:22.700",
    boxSerial: 30,
    serial: 2026011071,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:39:49.956",
    boxSerial: 30,
    serial: 2026011063,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:40:10.782",
    boxSerial: 30,
    serial: 2026011062,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:40:31.780",
    boxSerial: 30,
    serial: 2026011061,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:40:52.422",
    boxSerial: 30,
    serial: 2026011066,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:41:12.940",
    boxSerial: 30,
    serial: 2026011065,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:41:36.965",
    boxSerial: 30,
    serial: 2026011064,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:41:57.849",
    boxSerial: 30,
    serial: 2026011069,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.3,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.4,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:42:28.566",
    boxSerial: 30,
    serial: 2026011068,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
  {
    crate: null,
    timestampIso: "2026-04-27T09:42:47.877",
    boxSerial: 30,
    serial: 2026011067,
    unloadedLine: 28.7,
    loadedLineBeforeTester: 25.2,
    testerA: false,
    testerB: false,
    testerC: false,
    lineHarvestFail: false,
    inductance: null,
    inductancePassFail: "",
    failReason: null,
    observations: null,
    result: "Passed",
    delta: 3.5,
  },
];

/** `Timesheet` worksheet — first two populated rows. */
export const CLAMP_QC_TIMESHEET_ROWS: ClampQcTimesheetRow[] = [
  {
    dateIso: "2026-04-27",
    timeStart: "09:00",
    timeStop: "11:03",
    initials: "JR",
    completedUnits: 180,
    withLineTest: 0,
  },
  {
    dateIso: "2026-04-27",
    timeStart: "12:39:08",
    timeStop: "13:48:41",
    initials: "GM",
    completedUnits: 180,
    withLineTest: 0,
  },
];

/** `Parameters` worksheet. */
export const CLAMP_QC_INDUCTANCE_LIMITS: ClampQcInductanceLimits[] = [
  { label: "Small inductance", min: 1.05, max: 2.1 },
  { label: "Large inductance", min: null, max: null },
];

/** Built-in `Reasons for failure` strings (same source as the API). The live inspection list merges hub extras and sorts A–Z. */
export const CLAMP_QC_FAILURE_REASONS: readonly string[] = CLAMP_QC_PHYSICAL_FAIL_REASONS_BUILTIN;

/** Count of passed rows on the `Large` tab in the GBO-764 sample file (workbook total). */
const GBO764_LARGE_PASSED_TOTAL = 180;

export interface ClampQcWorkOrderBundle {
  /** URL segment, for example `gbo-764`. */
  slug: string;
  /** Short label for lists and reporting. */
  title: string;
  status: "active" | "on_hold" | "complete";
  /** Megaventory `public.build_order.mv_status` when loaded from the hub API (mock bundles may omit). */
  mvStatus?: string | null;
  /** Mock `build_order.id` (Manufacturing-Dashboard style). */
  buildOrderId: number;
  /** Stock keeping unit / part number (clamp lines). */
  sku: string;
  /**
   * Mock count of units already recorded for this build (e.g. `serial_fci` / line count),
   * separate from workbook “passed on Large tab” totals.
   */
  quantityComplete: number;
  /** Manufacturer ship-by date (ISO `YYYY-MM-DD`). */
  mfrShipByDate: string;
  mfrReferenceNumber: string | null;
  /** Mock warehouse label (hub uses `public.location.name` for Smart Grid Solutions rows only). */
  locationCode: string;
  context: ClampQcWorkOrderContext;
  largeInspectionRows: ClampQcInspectionRow[];
  smallInspectionRows: ClampQcInspectionRow[];
  timesheetRows: ClampQcTimesheetRow[];
  /**
   * Total units passed on the Large tab as recorded for the job (may be higher than
   * `largeInspectionRows.length` when the grid shows a sample subset).
   */
  largePassedTotal: number;
}

const GBO801_LARGE_INSPECTION_ROWS = GBO764_LARGE_INSPECTION_ROWS.slice(0, 12);

const GBO812_LARGE_INSPECTION_ROWS: ClampQcInspectionRow[] = GBO764_LARGE_INSPECTION_ROWS.slice(0, 10).map((row, index) =>
  index === 5
    ? {
        ...row,
        result: "Failed",
        failReason: "Wire damage",
        observations: "Repeat check after rework.",
        delta: 4.1,
      }
    : row,
);

export const CLAMP_QC_WORK_ORDER_BUNDLES: ClampQcWorkOrderBundle[] = [
  {
    slug: "gbo-764",
    title: "GBO-764 — 1200 clamp QC",
    status: "active",
    buildOrderId: 100764,
    sku: "90-003-QC",
    quantityComplete: 3420,
    mfrShipByDate: "2026-05-18",
    mfrReferenceNumber: "MFR-8831-L1",
    locationCode: "Smart Grid Solutions",
    context: {
      workOrder: "GBO-764",
      drawingRevision: "1200 × 90-003-QC",
      purchaseOrder: "GPO1804",
      plannedQuantityFromWorkbook: 7200,
    },
    largeInspectionRows: GBO764_LARGE_INSPECTION_ROWS,
    smallInspectionRows: [],
    timesheetRows: CLAMP_QC_TIMESHEET_ROWS,
    largePassedTotal: GBO764_LARGE_PASSED_TOTAL,
  },
  {
    slug: "gbo-801",
    title: "GBO-801 — 1500 line pilot",
    status: "active",
    buildOrderId: 100801,
    sku: "90-003-QC",
    quantityComplete: 920,
    mfrShipByDate: "2026-05-02",
    mfrReferenceNumber: "PILOT-19",
    locationCode: "Smart Grid Solutions — Shop floor B",
    context: {
      workOrder: "GBO-801",
      drawingRevision: "1500 × 45-010-QC",
      purchaseOrder: "GPO1902",
      plannedQuantityFromWorkbook: 2400,
    },
    largeInspectionRows: GBO801_LARGE_INSPECTION_ROWS,
    smallInspectionRows: [],
    timesheetRows: [
      {
        dateIso: "2026-04-28",
        timeStart: "08:15",
        timeStop: "10:40",
        initials: "AK",
        completedUnits: 96,
        withLineTest: 12,
      },
      {
        dateIso: "2026-04-28",
        timeStart: "13:05",
        timeStop: "15:22",
        initials: "JR",
        completedUnits: 88,
        withLineTest: 0,
      },
    ],
    largePassedTotal: 118,
  },
  {
    slug: "gbo-812",
    title: "GBO-812 — retrofit batch",
    status: "on_hold",
    buildOrderId: 100812,
    sku: "90-002-QC",
    quantityComplete: 1200,
    mfrShipByDate: "2026-06-01",
    mfrReferenceNumber: null,
    locationCode: "Smart Grid Solutions",
    context: {
      workOrder: "GBO-812",
      drawingRevision: "980 × 22-007-QC",
      purchaseOrder: "GPO2011",
      plannedQuantityFromWorkbook: 5000,
    },
    largeInspectionRows: GBO812_LARGE_INSPECTION_ROWS,
    smallInspectionRows: [],
    timesheetRows: [
      {
        dateIso: "2026-04-26",
        timeStart: "07:45",
        timeStop: "09:30",
        initials: "GM",
        completedUnits: 44,
        withLineTest: 0,
      },
    ],
    largePassedTotal: 312,
  },
];

/**
 * Development-only job URL segment. Resolved only when `import.meta.env.DEV` is true so production never treats this
 * slug as a mock job. It is not part of `CLAMP_QC_WORK_ORDER_BUNDLES` and does not appear in mock reporting helpers.
 */
export const CLAMP_QC_LOCAL_SANDBOX_SLUG = "local-sandbox";

const CLAMP_QC_LOCAL_SANDBOX_BUNDLE: ClampQcWorkOrderBundle = {
  slug: CLAMP_QC_LOCAL_SANDBOX_SLUG,
  title: "LOCAL — inspection touch sandbox (development)",
  status: "active",
  buildOrderId: 9_000_001,
  sku: "90-002-QC",
  quantityComplete: 0,
  mfrShipByDate: "2026-04-27",
  mfrReferenceNumber: null,
  locationCode: "Local workstation",
  context: {
    workOrder: "LOCAL-SANDBOX",
    drawingRevision: "Development · small clamp line",
    purchaseOrder: "DEV-LOCAL",
    plannedQuantityFromWorkbook: 9999,
  },
  largeInspectionRows: [],
  smallInspectionRows: [],
  timesheetRows: [],
  largePassedTotal: 0,
};

export function clampQcBundleBySlug(slug: string | undefined): ClampQcWorkOrderBundle | undefined {
  if (slug == null || slug.trim() === "") {
    return undefined;
  }
  const normalized = slug.trim().toLowerCase();
  return CLAMP_QC_WORK_ORDER_BUNDLES.find((bundle) => bundle.slug === normalized);
}

export function clampQcResolveBundleForSlug(slug: string | undefined): ClampQcWorkOrderBundle | undefined {
  if (slug == null || slug.trim() === "") {
    return undefined;
  }
  const normalized = slug.trim().toLowerCase();
  if (import.meta.env.DEV && normalized === CLAMP_QC_LOCAL_SANDBOX_SLUG) {
    return CLAMP_QC_LOCAL_SANDBOX_BUNDLE;
  }
  return clampQcBundleBySlug(slug);
}

export function clampQcIsLocalSandboxSlug(slug: string | undefined): boolean {
  if (slug == null || slug.trim() === "") {
    return false;
  }
  return import.meta.env.DEV && slug.trim().toLowerCase() === CLAMP_QC_LOCAL_SANDBOX_SLUG;
}

/**
 * Clamp line follows the SKU prefix (Manufacturing-Dashboard alignment): `90-003*` is large clamp, `90-002*` is small clamp.
 */
export function clampInspectionSizeFromSku(sku: string): "large" | "small" | "unknown" {
  const trimmed = sku.trim().toUpperCase();
  if (trimmed.startsWith("90-003")) {
    return "large";
  }
  if (trimmed.startsWith("90-002")) {
    return "small";
  }
  return "unknown";
}

/**
 * Units per standard shipping carton for QC floor fill tallies, by clamp line SKU prefix (`90-003*` large, `90-002*` small).
 * Matches the shared QC workbook conditional-format bands: Large line 20 clamps per carton, Small line 30 per carton.
 */
export const CLAMP_QC_UNITS_PER_CARTON_LARGE = 20;
export const CLAMP_QC_UNITS_PER_CARTON_SMALL = 30;

/**
 * Resolved carton totals for SKU line prefixes (normally from `/api/clamp-qc/settings` stored as `hub.clampqc`).
 */
export interface ClampQcCartonUnitsPayload {
  unitsPerCartonLarge: number;
  unitsPerCartonSmall: number;
}

/**
 * Clamps per shipping carton using Large/Small targets (usually hub settings).
 */
export function clampInspectionUnitsPerCartonForSku(sku: string, units: ClampQcCartonUnitsPayload): number | null {
  const clampLine = clampInspectionSizeFromSku(sku);
  if (clampLine === "large") {
    return units.unitsPerCartonLarge;
  }
  if (clampLine === "small") {
    return units.unitsPerCartonSmall;
  }
  return null;
}

/**
 * Offline fallback aligned with hub settings seed defaults (`hub.clampqc`).
 */
export function clampInspectionUnitsPerBoxFromSku(sku: string): number | null {
  return clampInspectionUnitsPerCartonForSku(sku, {
    unitsPerCartonLarge: CLAMP_QC_UNITS_PER_CARTON_LARGE,
    unitsPerCartonSmall: CLAMP_QC_UNITS_PER_CARTON_SMALL,
  });
}

export type ClampQcInspectionRowTagged = ClampQcInspectionRow & { clampSize: "large" | "small" };

/** All inspection lines for a bundle (large and small), tagged for filtering and reporting drill-down. */
export function clampQcTaggedInspectionRows(bundle: ClampQcWorkOrderBundle): ClampQcInspectionRowTagged[] {
  return [
    ...bundle.largeInspectionRows.map((row) => ({ ...row, clampSize: "large" as const })),
    ...bundle.smallInspectionRows.map((row) => ({ ...row, clampSize: "small" as const })),
  ];
}

export function clampQcDistinctLocationCodes(): string[] {
  const set = new Set(CLAMP_QC_WORK_ORDER_BUNDLES.map((bundle) => bundle.locationCode));
  return [...set].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
}

export function clampQcDistinctSkus(): string[] {
  const set = new Set(CLAMP_QC_WORK_ORDER_BUNDLES.map((bundle) => bundle.sku));
  return [...set].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
}

export interface ClampQcWorkOrderReportRow {
  slug: string;
  title: string;
  status: ClampQcWorkOrderBundle["status"];
  workOrder: string;
  sku: string;
  locationCode: string;
  plannedQuantity: number;
  largePassedBookTotal: number;
  largeFailInGrid: number;
  largePassInGrid: number;
  gridRowCount: number;
  avgDeltaSample: number | null;
}

function avgDelta(rows: ClampQcInspectionRow[]): number | null {
  const deltas = rows.map((row) => row.delta).filter((value): value is number => value != null);
  if (deltas.length === 0) {
    return null;
  }
  return deltas.reduce((accumulator, value) => accumulator + value, 0) / deltas.length;
}

export function clampQcPerWorkOrderReportRows(): ClampQcWorkOrderReportRow[] {
  return CLAMP_QC_WORK_ORDER_BUNDLES.map((bundle) => {
    const large = bundle.largeInspectionRows;
    const largePassInGrid = large.filter((row) => row.result.toLowerCase() === "passed").length;
    const largeFailInGrid = large.filter((row) => row.result.toLowerCase() !== "passed").length;
    return {
      slug: bundle.slug,
      title: bundle.title,
      status: bundle.status,
      workOrder: bundle.context.workOrder,
      sku: bundle.sku,
      locationCode: bundle.locationCode,
      plannedQuantity: bundle.context.plannedQuantityFromWorkbook,
      largePassedBookTotal: bundle.largePassedTotal,
      largeFailInGrid,
      largePassInGrid,
      gridRowCount: large.length,
      avgDeltaSample: avgDelta(large),
    };
  });
}

export function clampQcOverallReportAggregate(): {
  workOrderCount: number;
  plannedQuantitySum: number;
  largePassedBookSum: number;
  failRowsInSamples: number;
} {
  const rows = clampQcPerWorkOrderReportRows();
  return {
    workOrderCount: rows.length,
    plannedQuantitySum: rows.reduce((accumulator, row) => accumulator + row.plannedQuantity, 0),
    largePassedBookSum: rows.reduce((accumulator, row) => accumulator + row.largePassedBookTotal, 0),
    failRowsInSamples: rows.reduce((accumulator, row) => accumulator + row.largeFailInGrid, 0),
  };
}

/** @deprecated Use `clampQcBundleBySlug("gbo-764")` or bundles list. */
export const CLAMP_QC_WORK_ORDER: ClampQcWorkOrderContext = CLAMP_QC_WORK_ORDER_BUNDLES[0].context;

/** @deprecated Use bundle `largeInspectionRows` for the active work order. */
export const CLAMP_QC_LARGE_INSPECTION_ROWS: ClampQcInspectionRow[] =
  CLAMP_QC_WORK_ORDER_BUNDLES[0].largeInspectionRows;

/** @deprecated Use bundle field `largePassedTotal`. */
export const CLAMP_QC_LARGE_PASSED_COUNT = CLAMP_QC_WORK_ORDER_BUNDLES[0].largePassedTotal;
